import { i18next } from '../i18n';

export type VoiceCommandParam = {
    id: string;
    hintTier: 1 | 2;
};

export type VoiceCommandDoc = {
    id: string;
    icon: string;
    name: string;
    params: VoiceCommandParam[];
};

export const VOICE_COMMAND_DOCS: VoiceCommandDoc[] = [
    {
        id: 'quest',
        icon: 'add_circle',
        name: 'Create quest',
        params: [
            { id: 'category', hintTier: 1 },
            { id: 'dueDate', hintTier: 1 },
            { id: 'repeat', hintTier: 1 },
            { id: 'focusMode', hintTier: 2 },
            { id: 'difficulty', hintTier: 2 },
        ],
    },
    {
        id: 'postpone',
        icon: 'schedule',
        name: 'Postpone quest',
        params: [
            { id: 'quest', hintTier: 1 },
            { id: 'newDate', hintTier: 1 },
        ],
    },
    {
        id: 'category',
        icon: 'category',
        name: 'Create category',
        params: [
            { id: 'color', hintTier: 1 },
            { id: 'icon', hintTier: 1 },
        ],
    },
    {
        id: 'achievement',
        icon: 'emoji_events',
        name: 'Create achievement',
        params: [
            { id: 'quest', hintTier: 1 },
            { id: 'metric', hintTier: 1 },
            { id: 'category', hintTier: 1 },
            { id: 'icon', hintTier: 2 },
            { id: 'rarity', hintTier: 2 },
        ],
    },
];

export function sortVoiceParams(params: VoiceCommandParam[]): VoiceCommandParam[] {
    return [...params].sort((a, b) => a.hintTier - b.hintTier || a.id.localeCompare(b.id));
}

export function hintVisibleParams(cmd: VoiceCommandDoc): {
    chips: VoiceCommandParam[];
    moreCount: number;
} {
    const sorted = sortVoiceParams(cmd.params);
    const qualifying = sorted.filter(p => p.hintTier === 1);
    const chips = qualifying.slice(0, 4);
    const chipSet = new Set(chips);
    const moreCount = sorted.filter(p => !chipSet.has(p)).length;
    return { chips, moreCount };
}

export function trySayingSuffix(cmd: VoiceCommandDoc): string {
    const sorted = sortVoiceParams(cmd.params);
    return sorted
        .slice(0, 2)
        .map(p => voiceCommandParamExample(cmd, p))
        .filter(Boolean)
        .join(', ');
}

const voiceCommandParamTranslationKeys = {
    'quest.category': { label: 'voiceDocs.quest.category.label', example: 'voiceDocs.quest.category.example' },
    'quest.dueDate': { label: 'voiceDocs.quest.dueDate.label', example: 'voiceDocs.quest.dueDate.example' },
    'quest.repeat': { label: 'voiceDocs.quest.repeat.label', example: 'voiceDocs.quest.repeat.example' },
    'quest.focusMode': { label: 'voiceDocs.quest.focusMode.label', example: 'voiceDocs.quest.focusMode.example' },
    'quest.difficulty': { label: 'voiceDocs.quest.difficulty.label', example: 'voiceDocs.quest.difficulty.example' },
    'postpone.quest': { label: 'voiceDocs.postpone.quest.label', example: 'voiceDocs.postpone.quest.example' },
    'postpone.newDate': { label: 'voiceDocs.postpone.newDate.label', example: 'voiceDocs.postpone.newDate.example' },
    'category.color': { label: 'voiceDocs.category.color.label', example: 'voiceDocs.category.color.example' },
    'category.icon': { label: 'voiceDocs.category.icon.label', example: 'voiceDocs.category.icon.example' },
    'achievement.quest': { label: 'voiceDocs.achievement.quest.label', example: 'voiceDocs.achievement.quest.example' },
    'achievement.metric': { label: 'voiceDocs.achievement.metric.label', example: 'voiceDocs.achievement.metric.example' },
    'achievement.category': { label: 'voiceDocs.achievement.category.label', example: 'voiceDocs.achievement.category.example' },
    'achievement.icon': { label: 'voiceDocs.achievement.icon.label', example: 'voiceDocs.achievement.icon.example' },
    'achievement.rarity': { label: 'voiceDocs.achievement.rarity.label', example: 'voiceDocs.achievement.rarity.example' },
} as const;

function voiceCommandParamTranslationKey(cmd: VoiceCommandDoc, param: VoiceCommandParam) {
    return voiceCommandParamTranslationKeys[`${cmd.id}.${param.id}` as keyof typeof voiceCommandParamTranslationKeys];
}

export function voiceCommandParamLabel(cmd: VoiceCommandDoc, param: VoiceCommandParam): string {
    return i18next.t(voiceCommandParamTranslationKey(cmd, param).label);
}

export function voiceCommandParamExample(cmd: VoiceCommandDoc, param: VoiceCommandParam): string {
    return i18next.t(voiceCommandParamTranslationKey(cmd, param).example);
}
